import React from 'react'
import styled from 'styled-components'
import SegmentedControl from '../components/SegmentedControl'
import Helmet from 'react-helmet'
import favicon from '../images/favicon.ico'
import { BLOCKS } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Link } from 'gatsby'
import blogListings from '../../content/blogListings.json'
import Breadcrumbs from '../components/Breadcrumbs'
import Button from '../components/Button'
import ComponentRenderer from '../components/ComponentRenderer'
import Container from '../Layout/Container'
import CTA from '../sections/CTA'
import FacebookLogo from '../assets/svg/facebook.svg'
import Form from '../components/Form'
import FormSection from '../sections/Form'
import Layout from '../Layout/Layout'
import QuoteSVG from '../assets/svg/quote.svg'
import SEO from '../components/seo'
import Sticky from 'react-stickynode'
import SVG from 'react-inlinesvg'
import TextHeader from '../components/TextHeader'
import TwitterLogo from '../assets/svg/twitter.svg'

const StyledBlogPost = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 64px;
`

const BlogContainer = styled.div`
  margin: 0 auto;
  padding: 200px 24px 0 24px;
  position: relative;

  ${({ theme }) => theme.media.desktop} {
    max-width: ${({ fullWidth }) => (fullWidth === true ? '100%' : '608px')};
  }
`

const StyledCategoryBar = styled.ul``

const StyledCategoryPill = styled.li`
  background-color: ${({ color }) => color};
  border-radius: 500px;
  color: #fff;
  display: inline-block;
  font-size: 8px;
  font-weight: bold;
  letter-spacing: 1.78px;
  line-height: 1.5;
  padding: 2px 8px;
  text-transform: uppercase;
`

const StyledBlogTitle = styled.h1`
  font-size: 2.125em;
  font-weight: 500;
  line-height: 1.41;
  margin: 16px 0;
`

const StyledBlogAuthor = styled.div`
  display: flex;
`

const StyledBlogAuthorAvatarIMG = styled.img`
  border-radius: 50%;
  height: 20px;
  width: 20px;
`

const StyledBlogAuthorData = styled.div`
  color: ${({ theme }) => theme.colors.greyParagraphColor};
  font-size: 0.75em;
  line-height: 1.67;
  margin-left: 8px;
`

const StyledFeaturedImageContainer = styled.section`
  align-items: center;
  // Default background is white
  background-color: ${({ bgColor }) => bgColor || 'white'};
  display: flex;
  margin: 48px 0;
  overflow: hidden;
  // Keeps enough space for the image to lazy load without causing 'screen jump'
  padding-top: ${({ ratioWidthHeight = 1 }) => (100 / ratioWidthHeight) + '%'};
  position: relative;
  width: 100%;

  img {
    position: absolute;
    top: 0;
    width: 100%;
  }
  
  ${({ theme }) => theme.media.maxTablet} {
    // Calculate the height from the space available on mobile/tablet
    height: ${({ ratioWidthHeight = 1 }) => `calc( (100vw - 48px ) / ${ratioWidthHeight})`};
    max-height: 340px;
    padding-top: 0;
    
    img {
      position: relative;
      top: unset;
    }
  }
`

const StyledEmbeddedImg = styled.img`
  border-radius: 8px;
  height: auto;
  margin-bottom: 24px;
  width: 100%;
`

const StyledImageDescription = styled.p`
  color: ${({ theme }) => theme.colors.greyParagraphColor};
  font-size: 0.75em !important;
  margin-bottom: 48px;
  text-align: center;
`

const StyledRichText = styled.div`
  border-bottom: 1px solid #e9ebef;
  margin-bottom: 24px;
  padding-bottom: 24px;
  position: relative;

  > p {
    color: ${({ theme }) => theme.colors.greyExcerptColor};
    font-size: 0.875em;
    line-height: 24px;
    margin-bottom: 24px;

    > a {
      text-decoration: underline;
    }

    > code {
      background-color: ${({ theme }) => theme.colors.greyBG};
      border-radius: 4px;
      border: 1px solid #8a94a6;
      box-sizing: unset;
      display: inline-block;
      font-size: 12px;
      padding: 2px 4px;
      white-space: pre;
    }
  }

  > ul, ol {
    margin: 24px 0;

    a {
      text-decoration: underline;
    }
  }

  > ul {
    li {
      color: #4e5d78;
      display: flex;
      font-size: 14px;
      line-height: 1.40;

      &::before {
        color: #8a94a6;
        content: '•';
      }

      > p {
        margin-left: 16px;
      }
    }
  }

  > ol {
    counter-reset: li;

    li {
      color: #4e5d78;
      counter-increment: li;
      display: flex;
      font-size: 14px;
      line-height: 1.41;

      &::before {
        content: counter(li) '.';
        color: #8a94a6;
        display: inline-block;
        width: 1em;
      }

      > p {
        margin-left: 16px;
      }
    }
  }

  > h1 {
    font-size: 43px;
    line-height: 48px;
    margin-bottom: 16px;
  }

  > h2 {
    font-size: 28px;
    line-height: 40px;
    margin-bottom: 12px;
  }

  > h3 {
    font-size: 24px;
    line-height: 36px;
    margin-bottom: 10px;
  }

  > h4 {
    color: #0a1f44;
    font-size: 1em;
    font-weight: 500;
    line-height: 30px;
    margin-bottom: 8px;
  }

  h5 {
    font-size: 14px;
    line-height: 28px;
    margin-bottom: 6px;
  }

  h6 {
    font-size: 12px;
    line-height: 24px;
    margin-bottom: 4px;
  }
`

const StyledTags = styled.div`
  color: #4e5d78;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.875em;
  font-weight: 500;
  margin-bottom: 8px;

  a {
    color: #506174;
    font-weight: 400;
    margin: 0 5px;
    text-decoration: underline;
  }
`

const StyledLastUpdated = styled.p`
  color: #506174;
  font-size: 0.875em;
  line-height: 24px;
  margin-bottom: 36px;
`

const StyledBlogBottom = styled.div`
  padding-bottom: 88px;
`

const StyledBackButton = styled.div`
  align-items: center;
  border-radius: 50%;
  box-shadow: 0 3px 3px -1px rgba(10, 22, 70, 0.1),
    0 0 1px 0 rgba(10, 22, 70, 0.06);
  cursor: pointer;
  display: flex;
  height: 48px;
  justify-content: center;
  left: -104px;
  position: absolute;
  top: 303px;
  transition: 250ms;
  width: 48px;

  svg {
    transition: 250ms;
  }

  &:hover {
    box-shadow: 0 6px 6px -1px rgba(10, 22, 70, 0.1),
    0 0 1px 0 rgba(10, 22, 70, 0.06);

    svg {
      transform: translateX(-2px);
    }
  }
`

const StyledShareButtons = styled.div`
  display: none;
  flex-direction: column;
  left: -104px;
  position: absolute;
  top: -10px;

  a {
    margin-bottom: 16px;
  }

  ${({ theme }) => theme.media.desktop} {
    display: flex;
  }
`

const StyledBlockquote = styled.blockquote`
  margin-bottom: 48px;
  margin-top: 48px;
  position: relative;

  p {
    font-size: 20px;
    font-weight: 300;
    line-height: 32px;

    &:last-child {
      color: #8a94a6;
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 1.2px;
      line-height: 16px;
      margin-top: 24px;
      text-transform: uppercase;
      white-space: pre-line;
    }
  }
`

const StyledQuoteSign = styled(SVG)`
  left: -16px;
  position: absolute;\
  top: -16px;
  transform: rotate(180deg);

  svg {
    height: auto;
    width: 128px;
  }
`


const Blockquote = ({ children }) => (
  <StyledBlockquote>
    <StyledQuoteSign src={QuoteSVG} />
    {children}
  </StyledBlockquote>
)

// Expects post slug to contain exactly 1 backslash
const buildBreadcrumbs = post =>
  post.slug.split('/').map((item, index) => ({
    name: index ? post.name : post.listing,
    path: index ? '' : '/' + item,
  }))

const LazyFeaturedImg = ({ fluid: { aspectRatio, srcSetWebp }, bgColor = '#1B2B47' }) =>
  <StyledFeaturedImageContainer bgColor={bgColor} ratioWidthHeight={aspectRatio}>
    <img alt={'featured'} srcSet={srcSetWebp} loading="lazy" />
  </StyledFeaturedImageContainer>

const ImageWithDescription = ({ img }) => (
  <div>
    <StyledEmbeddedImg src={img.data.target.fields.file['en-US'].url} loading='lazy'/>
    {img.data.target.fields.description && (
      <StyledImageDescription>
        {img.data.target.fields.description['en-US']}
      </StyledImageDescription>
    )}
  </div>
)

const string_to_slug = str => {
  str = str.replace(/^\s+|\s+$/g, '') // trim
  str = str.toLowerCase()

  // remove accents, swap ñ for n, etc
  var from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;'
  var to = 'aaaaeeeeiiiioooouuuunc------'
  for (var i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i))
  }

  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-') // collapse dashes

  return str
}

const toTagId = tag => tag.id

const BlogPost = ({ pageContext }) => {
  const addedIndexes = []
  const length = blogListings.length
  const post = require('../../content/' + pageContext.slug.replace(/\//g, '--'))
  const postTagIds = (post.tags || []).map(toTagId)
  const related = []

  // image for social media when linked to blog post
  const socialImg = "https:" + post.featuredImage.fluid.src

  // Get up to 10 related posts, by tags
  if (postTagIds.length) {
    for (let index = 0; related.length < 10 && index < length; index++) {
      const { tags } = blogListings[index]

      // If blogListings includes any of the same tags as this post tags
      if ((tags || []).map(toTagId).some(id => postTagIds.includes(id))) {
        // Save index to avoid dupes
        if (post.name !== blogListings[index].name) {
          addedIndexes.push(index)
          related.push(blogListings[index])
        }
      }
    }
  }

  // If still fewer than 10 related, add by category match
  for (let index = 0; related.length < 10 && index < length; index++) {
    const { category } = blogListings[index]

    // If matching category name and wasn't previously added
    if (category.name === post.category.name && !addedIndexes.includes(index)) {
      related.push(blogListings[index])
    }
  }

  const popular = blogListings.filter(item => item.popular).slice(0, 10)

  const FormData = {
    confirmMessage:
      '#### Awesome, great content coming to your inbox ' +
      'very soon!\nWe just shot you an email to verify you’re really you.\n' +
      'Just click the confirmation link and you’ll be all set.',
    items: [
      {
        fields: {
          name: 'Embed Form',
          content: '<iframe loading="lazy" src="https://hello.learn.mailgun.com/l/830283/2019-12-19/bw8" width="100%" height="260" type="text/html" frameborder="0" allowTransparency="true" style="border: 0"></iframe>',
          formType: "blog-post-form"
        },
      },
    ],
    name: 'Blog Post Form',
    buttonText: 'Count Me In!',
    formType: 'blog-post'
  }

  const FormSectionData = {
    centered: true,
    fullWidth: true,
    greyBG: true,
    name: 'Blog Post Form',
    sectionType: 'Form',
    items: [
      {
        fields: {
          text:
            'No spam, ever. Only musings and writings from the Mailgun team.',
          title: 'Always be in the know and grab free email resources!',
          themeSize: 'tiny',
          themeAlign: 'center',
        },
        sys: {
          id: '1lEyOwdthmbQT8ALzdh7Ng',
          contentType: {
            sys: {
              id: 'textHeader',
            },
          },
        },
      },
      {
        fields: {
          formType: 'blog-post-form'
        }
      },
    ],
  }

  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: node => <ImageWithDescription img={node} />,
      [BLOCKS.EMBEDDED_ENTRY]: node => (
        <ComponentRenderer
          tag={node.data.target.sys.contentType.sys.id}
          cmsData={node.data.target.fields}
        />
      ),
      [BLOCKS.QUOTE]: (node, children) => (
        <Blockquote node={node} children={children} />
      ),
    },
  }

  return (
    <Layout blog="true" singlePost={true} overflowHidden>
      <SEO
        title={post.slug}
        url={`https://www.mailgun.com/${post.slug}`}
        type="article"
      />
      <Helmet defer={false}>
        <title>{pageContext.seo.title}</title>
        <meta name="description" content={pageContext.seo.description} />
        <meta
          content={pageContext.seo.robots || 'index,follow'}
          name="robots"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageContext.seo.canonical} />
        <meta property="og:title" content={pageContext.seo.title} />
        <meta property="og:description" content={pageContext.seo.description} />
        <meta property="og:site_name" content="Mailgun" />
        <meta property="og:image" content={socialImg} />

        <meta name="twitter:title" content={post.name} />
        <meta name="twitter:description" content={pageContext.seo.description} />
        <meta name="twitter:image" content={socialImg} />
        <link rel="canonical" href={pageContext.seo.canonical} />
        <link rel="icon" href={favicon} />
      </Helmet>
      <BlogContainer>
        <StyledBackButton
          onClick={() => typeof window !== 'undefined' && window.history.back()}
        >
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path
              fill="#697788"
              fillRule="evenodd"
              d="M7.4 14a.99.99 0 0 0 0-1.4L3.8 9H15a1 1 0 0 0 0-2H3.8l3.6-3.6A.99.99 0 0 0 6 2L1.414 6.586a2 2 0 0 0 0 2.828L6 14a.99.99 0 0 0 1.4 0z"
            />
          </svg>
        </StyledBackButton>

        <Breadcrumbs items={buildBreadcrumbs(post)} />

        <StyledBlogPost>
          <StyledCategoryBar>
            <StyledCategoryPill color={post.category.color}>
              {post.category.name}
            </StyledCategoryPill>
          </StyledCategoryBar>
          <StyledBlogTitle>{post.name}</StyledBlogTitle>
          <StyledBlogAuthor>
            {post.author.avatar && (
              <StyledBlogAuthorAvatarIMG src={post.author.avatar.file.url} />
            )}
            <StyledBlogAuthorData>{post.author.name}</StyledBlogAuthorData>
            <StyledBlogAuthorData>
              • {post.readTime || 5} min read
            </StyledBlogAuthorData>
          </StyledBlogAuthor>
          {post.featuredImage && (
            // We might provide a custom background color, default is '#1B2B47'
            <LazyFeaturedImg fluid={post.featuredImage.fluid} bgColor={post.featuredImageBackground} />
          )}
          <StyledRichText id="contenful_richText">
            <Sticky
              enabled={true}
              top={200}
              bottomBoundary="#contenful_richText"
            >
              <StyledShareButtons>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${pageContext.seo.canonical}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SVG src={FacebookLogo} />
                </a>

                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://twitter.com/intent/tweet/?text=${post.name}&url=${pageContext.seo.canonical}`}
                >
                  <SVG src={TwitterLogo} />
                </a>
              </StyledShareButtons>
            </Sticky>
            {documentToReactComponents(post.content.json, renderOptions)}
          </StyledRichText>
        </StyledBlogPost>
        {post.tags && (
          <StyledTags>
            Tags:{' '}
            {post.tags.map(item => (
              <Link key={item.id} to={`/blog/tag/${string_to_slug(item.name)}`}>
                {item.name}
              </Link>
            ))}
          </StyledTags>
        )}

        <StyledLastUpdated>Last updated on {post.updatedAt}</StyledLastUpdated>
      </BlogContainer>
      <Container greyBG fullWidth>
        <StyledBlogBottom>
          <SegmentedControl
            tabList={[
              { id: '01', title: 'Related posts', content: related },
              {
                id: '02',
                title: 'Recent posts',
                content: blogListings.slice(0, 10),
              },
              { id: '03', title: 'Top posts', content: popular },
            ]}
            additionalTab="View all"
            additionalTabLink="/blog"
          />
          <FormSection noShift={true} cmsData={FormSectionData}>
            <TextHeader
              title={
                <p className="blog-form-title">Always be in the know and grab free email resources!</p>
              }
              text="No spam, ever. Only musings and writings from the Mailgun team."
              theme={{
                align: 'center',
                size: 'tiny',
                width: 'wide',
              }}
            />
            <Form cmsData={FormData} />
          </FormSection>
        </StyledBlogBottom>
      </Container>
      <Container>
        {/*<DisqusContainer>*/}
        {/*  <Disqus />*/}
        {/*</DisqusContainer>*/}
      </Container>
      <Container fullWidth>
        <CTA ghost slick greyBG>
          <TextHeader
            title={
              <div className="title">
                It's easy to get started. <span>And it's free.</span>
              </div>
            }
            subtitle="sign up"
            text="See what you can accomplish with the world's best email delivery platform."
            theme={{
              size: 'big',
              align: 'center',
            }}
          />
          <Button
            primary="true"
            xxl="true"
            to="https://signup.mailgun.com/"
            external
            isAbsolutePath
          >
            Sign up for Free
          </Button>
        </CTA>
      </Container>
    </Layout>
  )
}

export default BlogPost
